import { FC, PropsWithChildren, useEffect, useMemo } from 'react';

import { Box, Button, Stack } from '@mui/material';
import { FieldValues, UseFormReturn } from 'react-hook-form';

import { useGetAccounts, useGetUsers } from 'features/users/api';

import { TAccounts } from 'shared/services/types/accounts.types';
import { TFiltersMethod, TFiltersWithdrawal } from 'shared/services/types/common.types';
import { TUsers } from 'shared/services/types/users.types';
import { FormWrapper } from 'shared/ui/form-wrapper';
import { ModalTemplate } from 'shared/ui/modal-template';
import { hasFormValuesChanged } from 'shared/utils';

import { MethodFields } from './MethodFields';
import { WithdrawalFields } from './WithdrawalFields';

interface IProps extends PropsWithChildren {
  methodID: number;
  methods: UseFormReturn<FieldValues, any, undefined>;
  onSubmit: (data: TFiltersWithdrawal | TFiltersMethod) => void;
  onClose: () => void;
  isFilterOpen: boolean;
  setIsFilterOpen: any;
  userData: any;
  currentPage?: string;
}

export const FilterModal: FC<IProps> = ({
  methodID,
  methods,
  onSubmit,
  onClose,
  isFilterOpen,
  setIsFilterOpen,
  userData,
  currentPage
}) => {
  const {
    reset,
    formState: { isDirty, isValid }
  } = methods;
  const isConfirmationNeeded = hasFormValuesChanged(methods.watch(), userData ?? {});

  const defaultFilterAccs = {
    size: 1000,
    page: 0,
    order_by: 'method_id',
    method_id: methodID,
    is_desc_sort: true
  } as TAccounts.GetAccount;

  const defaultFilterUsers = {
    size: 1000,
    page: 0,
    order_by: 'method_id',
    method_id: methodID.toString(),
    is_desc_sort: true
  } as TUsers.GetUsers;

  const { isAccountsLoading, accounts } = useGetAccounts(defaultFilterAccs);

  const { isUsersLoading, usersData } = useGetUsers(defaultFilterUsers);

  const accSorted = useMemo(
    () =>
      accounts?.items.map(account => {
        const item = { value: account.account_id, label: account.account_id };
        return item;
      }),
    [accounts]
  );

  const usersSorted = useMemo(
    () =>
      usersData?.items.map(user => {
        const item = { value: user.id, label: user.email };
        return item;
      }),
    [usersData]
  );

  useEffect(() => {
    if (isFilterOpen && userData) {
      methods.reset(userData);
    }
  }, [isFilterOpen, userData]);

  useEffect(() => {
    methods.reset();
  }, [methodID]);

  const currentForm = () => {
    switch (currentPage) {
      case 'withdrawal':
        return (
          <WithdrawalFields
            accSorted={accSorted ?? []}
            isAccountsLoading={isAccountsLoading}
            usersSorted={usersSorted ?? []}
            isUsersLoading={isUsersLoading}
          />
        );
      default:
        return <MethodFields accSorted={accSorted ?? []} isAccountsLoading={isAccountsLoading} />;
    }
  };

  return (
    <ModalTemplate
      isConfirmationNeeded={isConfirmationNeeded}
      close={onClose}
      isOpen={isFilterOpen}
      titleText={'Filter'}
      reset={reset}>
      <FormWrapper onSubmit={onSubmit} methods={methods}>
        <Stack direction='column' alignItems='stretch' gap='30px' flexWrap='nowrap'>
          {currentForm()}
        </Stack>
        <Box
          mt={'30px'}
          display={'flex'}
          gap={'16px'}
          alignItems={'center'}
          justifyContent={'center'}>
          <Button
            variant='contained'
            fullWidth
            color='info'
            onClick={onClose}
            sx={{
              maxWidth: 175,
              m: 0,
              borderRadius: 50,
              textTransform: 'none',
              height: 40
            }}>
            Cancel
          </Button>
          <Button
            type='submit'
            fullWidth
            variant='contained'
            disabled={!isConfirmationNeeded || !isValid}
            sx={{
              m: 0,
              borderRadius: 50,
              textTransform: 'none',
              maxWidth: 175,
              height: 40,
              boxShadow: 'none'
            }}>
            Save
          </Button>
        </Box>
      </FormWrapper>
    </ModalTemplate>
  );
};
